import PropTypes from "prop-types";
import React, { useState, useEffect, useContext } from "react";
import { Grid, MenuItem } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";

import { FleetSettingsContext } from "../../../../context";
import { FilledTextField, FilledSelect } from "../../../../components/FormElements";
import formatNumber from "../../../../util/strings/formatNumber";

const EditFormField = ({
  type,
  field,
  unit,
  nestedIndex,
  nestedField,
  decorationProps,
  fieldSize,
  menuOptions,
  disabled,
  overrideTextFieldProps,
  overrideSelectProps,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { billing, setBillingSettingsForm, validateForm, invalids } =
    useContext(FleetSettingsContext);

  const [dirtyField, setDirtyField] = useState([]);
  const [fieldValue, setFieldValue] = useState("");
  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldPath, setFieldPath] = useState([]);
  const [fieldUnit, setFieldUnit] = useState(null);

  useEffect(() => {
    let label = t(`Fleet.Settings.BillingEdit.${field}`);
    if (nestedField !== "") {
      label = t(`Fleet.Settings.BillingEdit.${field}.${nestedField}`);
    }
    setFieldLabel(label);
  }, []);

  useEffect(() => {
    let value;
    let path;
    if (nestedIndex !== null && nestedField !== "") {
      value = billing[field][nestedIndex][nestedField];
      path = [field, nestedIndex, nestedField];
    } else if (nestedField !== "") {
      value = billing[field][nestedField];
      path = [field, nestedField];
    } else {
      value = billing[field];
      path = [field];
    }
    setFieldValue(value);
    setFieldPath(path);

    const unitOptions = {
      fixed: billing.currency,
      currency: billing.currency,
      percentage: "PERCENT_WHOLE",
      minutes: "MINUTES",
    };
    setFieldUnit(unitOptions[unit]);
  }, [billing]);

  useEffect(() => {
    validateForm("billing");
  }, [billing[field]]);

  const handleOnBlur = () => {
    validateForm("billing");
    if (dirtyField.length === 0) {
      setDirtyField([fieldPath]);
    }
  };

  const handleChange = ({ target: { value } }) => {
    const incomingValue = ["fixed", "currency"].includes(unit)
      ? formatNumber(value, "INTEGER")
      : value;
    if (nestedIndex !== null && nestedField !== "") {
      billing[field][nestedIndex][nestedField] = incomingValue;
    } else if (nestedField !== "") {
      billing[field][nestedField] = incomingValue;
    } else {
      billing[field] = incomingValue;
    }
    setBillingSettingsForm({ ...billing });
  };

  const getValidationError = () => {
    const pathArrStr = fieldPath
      .join(".")
      .replace(/(\.\d+)/g, "[$1]")
      .replace(/\[\./g, "[");
    const fieldIsDirty = dirtyField.length > 0 && dirtyField[0].join(".") === fieldPath?.join(".");
    return fieldIsDirty ? invalids.billing.find((err) => err.includes(pathArrStr)) : "";
  };

  const commonProps = {
    label: fieldLabel,
    value: fieldValue,
    onBlur: handleOnBlur,
    onChange: handleChange,
    helperText: getValidationError(),
  };

  return (
    <Grid item xs={12} md={fieldSize}>
      {type === "select" ? (
        <FilledSelect
          SelectProps={{
            style: {
              borderBottom: `1px solid ${theme.palette.text.lightgrey}`,
            },
            disabled,
          }}
          FormControlProps={{
            fullWidth: true,
            margin: "normal",
            error: Boolean(getValidationError()),
          }}
          {...commonProps}
          {...overrideSelectProps}
        >
          {menuOptions.map((option) => (
            <MenuItem key={option.value || option} value={option.value || option}>
              {option.label || option}
            </MenuItem>
          ))}
        </FilledSelect>
      ) : (
        <FilledTextField
          fullWidth
          mask={fieldUnit}
          underlineColor={theme.palette.text.lightgrey}
          error={Boolean(getValidationError(fieldPath))}
          disabled={disabled}
          type={type}
          {...commonProps}
          {...decorationProps}
          {...overrideTextFieldProps}
        />
      )}
    </Grid>
  );
};

const MenuOptionType = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

EditFormField.propTypes = {
  type: PropTypes.string,
  field: PropTypes.string.isRequired,
  unit: PropTypes.string,
  nestedIndex: PropTypes.number,
  nestedField: PropTypes.string,
  decorationProps: PropTypes.object,
  fieldSize: PropTypes.number,
  menuOptions: PropTypes.arrayOf(PropTypes.oneOfType([MenuOptionType, PropTypes.string])),
  disabled: PropTypes.bool,
  overrideTextFieldProps: PropTypes.object,
  overrideSelectProps: PropTypes.object,
};

EditFormField.defaultProps = {
  type: "text-field",
  unit: null,
  nestedIndex: null,
  nestedField: "",
  decorationProps: {},
  fieldSize: 4,
  menuOptions: [],
  disabled: false,
  overrideTextFieldProps: {},
  overrideSelectProps: {},
};

export default EditFormField;
